import { supabasePublic } from "../utils/supabaseClient";
const supabase = supabasePublic;
import {
  LibraryDocument,
  DocumentData,
  ProcessedSection,
  TemplateDocument,
} from "../types/document-library";

// Optional config for reusing this service across different contexts
export type TemplateServiceConfig = {
  supabaseClient?: typeof supabase;
  tableName?: string;
  bucketName?: string;
};

const DEFAULT_TABLE = "scopingai_proposaltemplates";
const DEFAULT_BUCKET = "proposaltemplates";

const getClient = (cfg?: TemplateServiceConfig) => cfg?.supabaseClient ?? supabase;
const getTable = (cfg?: TemplateServiceConfig) => cfg?.tableName ?? DEFAULT_TABLE;
const getBucket = (cfg?: TemplateServiceConfig) => cfg?.bucketName ?? DEFAULT_BUCKET;

// Get all templates — only from DB (user's own + public)
export const getAllTemplates = async (
  userId?: string,
  serviceConfig?: TemplateServiceConfig
): Promise<LibraryDocument[]> => {
  if (!userId) return [];
  const templates = await fetchProposalTemplates(userId, serviceConfig);
  console.log(`✅ Total templates: ${templates.length}`);
  return templates;
};

// Parse sections from JSON blocks
const parseJsonSections = (content: string): string[] => {
  if (!content) return [];

  try {
    const parsed = typeof content === "string" ? JSON.parse(content) : content;

    if (parsed.blocks && Array.isArray(parsed.blocks)) {
      const sections: string[] = [];

      parsed.blocks.forEach((block: any) => {
        if (
          block.type === "heading1" ||
          block.type === "heading2" ||
          block.type === "heading3"
        ) {
          const text = block.content?.text || block.content || "";
          if (text && text.trim()) {
            sections.push(text.trim());
          }
        }
      });

      return sections.length > 0 ? sections : ["Untitled Section"];
    }
  } catch (e) {
    return parseMarkdownSections(content);
  }

  return ["Untitled Section"];
};

// Parse sections from markdown content (fallback for legacy templates)
const parseMarkdownSections = (content: string): string[] => {
  if (!content) return [];

  const lines = content.split("\n");
  const sections = [];

  for (const line of lines) {
    const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1]?.length || 0;
      const title = headerMatch[2]?.trim() || "";

      if (level <= 2 && title) {
        sections.push(title);
      }
    }
  }

  return sections.length > 0 ? sections : ["Template Content"];
};

// Fetch custom templates from database
export const fetchProposalTemplates = async (
  userId: string,
  serviceConfig?: TemplateServiceConfig
): Promise<LibraryDocument[]> => {
  const db = getClient(serviceConfig);
  const table = getTable(serviceConfig);
  const bucket = getBucket(serviceConfig);

  try {
    console.log("🔍 Fetching proposal templates for user:", userId);

    const { data, error } = await db
      .from(table)
      .select("*")
      .or(`user_id.eq.${userId},is_public.eq.true`)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("❌ Error fetching proposal templates:", error);
      return [];
    }

    console.log(
      `✅ Found ${data?.length || 0} proposal templates for user ${userId}`
    );

    const processedTemplates = await Promise.all(
      (data || []).map(async (template) => {
        let content = template.content || '';

        if (template.bucket_path) {
          try {
            console.log(`📁 Fetching content from bucket: ${template.bucket_path}`);

            const { data: fileData, error: downloadError } = await db.storage
              .from(bucket)
              .download(template.bucket_path);

            if (downloadError) {
              console.error(`❌ Error downloading from bucket: ${downloadError.message}`);
              content = template.content || '';
            } else {
              content = await fileData.text();
              console.log(`✅ Content loaded from bucket: ${content.length} characters`);
            }
          } catch (bucketError) {
            console.error(`❌ Error processing bucket content:`, bucketError);
            content = template.content || '';
          }
        }

        const sections =
          template.sections && template.sections.length > 0
            ? template.sections
            : parseJsonSections(content || "");

        console.log(
          `📋 Processing template: ${template.name} with ${sections.length} sections`
        );

        return {
          id: template.id.toString(),
          title: template.name,
          client: template.is_public ? "Public Template" : "Custom Template",
          date: template.created_at,
          type: template.category || "Custom",
          sections: sections,
          rawData: {
            content: content,
            sections: [],
            metadata: {
              isDefault: false,
              description: template.description || "",
              userId: template.user_id,
              updatedAt: template.updated_at,
              bucketPath: template.bucket_path,
              isCustomVersionOfDefault: false,
              category: template.category,
              isPublic: template.is_public,
            },
          },
        };
      })
    );

    return processedTemplates;
  } catch (error) {
    console.error("❌ Error fetching proposal templates:", error);
    return [];
  }
};

// Create custom template with bucket storage
export const createCustomTemplate = async (template: {
  name: string;
  description: string;
  content: string;
  sections?: string[];
  category?: string;
  userId: string;
  isCustomVersionOfDefault?: boolean;
  is_public?: boolean;
}, serviceConfig?: TemplateServiceConfig) => {
  const db = getClient(serviceConfig);
  const table = getTable(serviceConfig);
  const bucket = getBucket(serviceConfig);

  try {
    const sectionsToSave =
      template.sections || parseJsonSections(template.content);

    console.log("📋 Creating template with sections:", sectionsToSave);

    console.log(`💾 Saving template markdown to ${bucket} bucket...`);

    const sanitizedName = template.name.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '_');
    const timestamp = Date.now();

    let isJson = false;
    try {
      JSON.parse(template.content);
      isJson = true;
    } catch (e) {
      isJson = false;
    }

    const fileExtension = isJson ? 'json' : 'md';
    const fileName = `${sanitizedName}_${timestamp}.${fileExtension}`;
    const bucketPath = `${template.userId}/${fileName}`;

    console.log(`📁 Bucket path: ${bucketPath}`);
    console.log(`📊 Content length: ${template.content.length} characters (${isJson ? 'JSON' : 'Markdown'})`);

    const { error: uploadError } = await db.storage
      .from(bucket)
      .upload(bucketPath, template.content, {
        contentType: isJson ? 'application/json' : 'text/markdown',
        upsert: true,
      });

    if (uploadError) {
      console.error("❌ Error uploading to bucket:", uploadError);
      throw new Error(`Failed to upload markdown to bucket: ${uploadError.message}`);
    }

    console.log(`✅ Markdown uploaded successfully to bucket: ${bucketPath}`);

    const { data, error } = await db
      .from(table)
      .insert({
        name: template.name,
        description: template.description,
        markdown_path: bucketPath,
        bucket_path: bucketPath,
        content: null,
        sections: null,
        category: template.category || "Custom",
        user_id: template.userId,
        is_public: template.is_public ?? false,
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating template:", error);
      throw error;
    }

    console.log(`✅ Template record created successfully: ${data.id}`);
    return data;
  } catch (error) {
    console.error("Error creating template:", error);
    throw error;
  }
};

// Duplicate a template
export const duplicateTemplate = async (
  templateId: string,
  customizations: {
    name?: string;
    description?: string;
    content?: string;
    sections?: string[];
  },
  userId: string,
  serviceConfig?: TemplateServiceConfig
) => {
  const db = getClient(serviceConfig);
  const table = getTable(serviceConfig);
  const bucket = getBucket(serviceConfig);

  try {
    const { data: sourceTemplate, error } = await db
      .from(table)
      .select("*")
      .eq("id", templateId)
      .single();

    if (error || !sourceTemplate) {
      throw new Error("Template not found");
    }

    // Load content from bucket if needed
    let content = customizations.content || sourceTemplate.content || '';
    if (!customizations.content && sourceTemplate.bucket_path) {
      try {
        const { data: fileData } = await db.storage
          .from(bucket)
          .download(sourceTemplate.bucket_path);
        if (fileData) content = await fileData.text();
      } catch {
        // fallback to db content
      }
    }

    const result = await createCustomTemplate({
      name: customizations.name || sourceTemplate.name,
      description: customizations.description || sourceTemplate.description,
      content,
      sections: customizations.sections || sourceTemplate.sections,
      category: sourceTemplate.category || "Custom",
      userId,
      isCustomVersionOfDefault: false,
    }, serviceConfig);

    console.log(`✅ Duplicated template ${templateId} for user ${userId}`);
    return result;
  } catch (error) {
    console.error("Error duplicating template:", error);
    throw error;
  }
};

// Update custom template with bucket storage
export const updateCustomTemplate = async (
  templateId: string,
  updates: {
    name?: string;
    description?: string;
    content?: string;
    sections?: string[];
    category?: string;
    is_public?: boolean;
  },
  serviceConfig?: TemplateServiceConfig
) => {
  const db = getClient(serviceConfig);
  const table = getTable(serviceConfig);
  const bucket = getBucket(serviceConfig);
  try {
    let sectionsToUpdate = updates.sections;
    if (updates.content && !updates.sections) {
      sectionsToUpdate = parseJsonSections(updates.content);
      console.log("📋 Auto-extracted sections for update:", sectionsToUpdate);
    }

    let updateData: any = {
      ...(updates.name && { name: updates.name }),
      ...(updates.description && { description: updates.description }),
      ...(updates.category && { category: updates.category }),
      ...(updates.is_public !== undefined && { is_public: updates.is_public }),
      sections: null,
      updated_at: new Date().toISOString(),
    };

    if (updates.content) {
      const { data: currentTemplate, error: fetchError } = await db
        .from(table)
        .select("user_id, name, bucket_path")
        .eq("id", templateId)
        .single();

      if (fetchError) {
        console.error("Error fetching current template:", fetchError);
        throw fetchError;
      }

      console.log(`💾 Updating template content in bucket...`);

      const templateName = updates.name || currentTemplate.name;
      const sanitizedName = templateName.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '_');
      const timestamp = Date.now();

      let isJson = false;
      try {
        JSON.parse(updates.content);
        isJson = true;
      } catch (e) {
        isJson = false;
      }

      const fileExtension = isJson ? 'json' : 'md';
      const fileName = `${sanitizedName}_${timestamp}.${fileExtension}`;
      const bucketPath = `${currentTemplate.user_id}/${fileName}`;

      console.log(`📁 New bucket path: ${bucketPath}`);
      console.log(`📊 Content length: ${updates.content.length} characters (${isJson ? 'JSON' : 'Markdown'})`);

      const { error: uploadError } = await db.storage
        .from(bucket)
        .upload(bucketPath, updates.content, {
          contentType: isJson ? 'application/json' : 'text/markdown',
          upsert: true,
        });

      if (uploadError) {
        console.error("❌ Error uploading to bucket:", uploadError);
        throw new Error(`Failed to upload markdown to bucket: ${uploadError.message}`);
      }

      console.log(`✅ Markdown uploaded successfully to bucket: ${bucketPath}`);

      if (currentTemplate.bucket_path) {
        try {
          await db.storage
            .from(bucket)
            .remove([currentTemplate.bucket_path]);
          console.log(`🗑️ Deleted old file: ${currentTemplate.bucket_path}`);
        } catch (deleteError) {
          console.warn(`⚠️ Could not delete old file: ${currentTemplate.bucket_path}`, deleteError);
        }
      }

      updateData.markdown_path = bucketPath;
      updateData.bucket_path = bucketPath;
      updateData.content = null;
    }

    console.log("📝 Updating template with data:", updateData);

    const { error } = await db
      .from(table)
      .update(updateData)
      .eq("id", templateId);

    if (error) {
      console.error("Error updating template:", error);
      throw error;
    }

    console.log(`✅ Template ${templateId} updated successfully`);
  } catch (error) {
    console.error("Error updating template:", error);
    throw error;
  }
};

// Load template content from bucket for editing
export const loadTemplateContent = async (templateId: string, serviceConfig?: TemplateServiceConfig): Promise<string> => {
  const db = getClient(serviceConfig);
  const table = getTable(serviceConfig);
  const bucket = getBucket(serviceConfig);

  try {
    console.log(`📁 Loading template content for ID: ${templateId}`);

    const { data: template, error: fetchError } = await db
      .from(table)
      .select("bucket_path, markdown_path, content, name")
      .eq("id", templateId)
      .single();

    if (fetchError) {
      console.error("❌ Error fetching template:", fetchError);
      throw fetchError;
    }

    if (!template) {
      throw new Error("Template not found");
    }

    console.log("📄 Template found:", {
      name: template.name,
      bucketPath: template.bucket_path,
      markdownPath: template.markdown_path,
      hasDatabaseContent: !!template.content
    });

    const bucketPath = template.bucket_path || template.markdown_path;
    if (bucketPath) {
      try {
        console.log(`📁 Downloading content from bucket: ${bucketPath}`);

        const { data: fileData, error: downloadError } = await db.storage
          .from(bucket)
          .download(bucketPath);

        if (downloadError) {
          console.error(`❌ Error downloading from bucket: ${downloadError.message}`);
          if (template.content) {
            console.log("🔄 Falling back to database content");
            return template.content;
          }
          throw downloadError;
        }

        const content = await fileData.text();
        console.log(`✅ Content loaded from bucket: ${content.length} characters`);
        return content;
      } catch (bucketError) {
        console.error(`❌ Error processing bucket content:`, bucketError);

        if (template.content) {
          console.log("🔄 Falling back to database content");
          return template.content;
        }
        throw bucketError;
      }
    }

    if (template.content) {
      console.log("📄 Using database content");
      return template.content;
    }

    throw new Error("No content found for template");
  } catch (error) {
    console.error("❌ Error loading template content:", error);
    throw error;
  }
};

// Delete custom template and clean up bucket files
export const deleteCustomTemplate = async (templateId: string, serviceConfig?: TemplateServiceConfig) => {
  const db = getClient(serviceConfig);
  const table = getTable(serviceConfig);
  const bucket = getBucket(serviceConfig);

  try {
    const { data: template, error: fetchError } = await db
      .from(table)
      .select("bucket_path")
      .eq("id", templateId)
      .single();

    if (fetchError) {
      console.error("Error fetching template for deletion:", fetchError);
      throw fetchError;
    }

    const { error } = await db
      .from(table)
      .delete()
      .eq("id", templateId);

    if (error) {
      console.error("Error deleting template:", error);
      throw error;
    }

    if (template?.bucket_path) {
      try {
        const { error: deleteFileError } = await db.storage
          .from(bucket)
          .remove([template.bucket_path]);

        if (deleteFileError) {
          console.warn(`⚠️ Could not delete file from bucket: ${template.bucket_path}`, deleteFileError);
        } else {
          console.log(`🗑️ Deleted file from bucket: ${template.bucket_path}`);
        }
      } catch (bucketError) {
        console.warn(`⚠️ Error deleting file from bucket:`, bucketError);
      }
    }

    console.log(`✅ Template ${templateId} deleted successfully`);
  } catch (error) {
    console.error("Error deleting template:", error);
    throw error;
  }
};

// ─── Editor Template helpers ─────────────────────────────────────────────────

/**
 * Returns true when the stored content JSON represents a multi-page
 * ModernTemplateEditor state.
 * Supports two saved formats:
 *   Format A (ProposalTemplateEditor): { pages: [...], zoom }
 *   Format B (templateStorageService):  { editorState: { pages: [...] }, ... }
 */
export function isEditorTemplate(content: string): boolean {
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed.pages) && parsed.pages.length > 0) return true;
    if (Array.isArray(parsed.editorState?.pages) && parsed.editorState.pages.length > 0) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Extracts the proposal section names from an editor template's content JSON.
 * Skips "Cover Page" and "Table of Contents" pages — those are structural.
 */
export function extractEditorTemplateSections(content: string): string[] {
  try {
    const { pages } = JSON.parse(content);
    const sections: string[] = [];
    for (const page of pages ?? []) {
      const marker = (page.blocks ?? []).find(
        (b: any) => b.type === "section-marker"
      );
      const title: string = marker?.content?.title ?? page.name ?? "";
      if (title && !["Cover Page", "Table of Contents"].includes(title)) {
        sections.push(title);
      }
    }
    return sections;
  } catch {
    return [];
  }
}

// Additional functions for document library
export const makeDocumentPublic = async (documentId: string) => {
  console.log("Making document public:", documentId);
};

export const saveDocumentToStorage = async (document: DocumentData) => {
  console.log("Saving document to storage:", document);
};

export const fetchMarkdownContent = async (path: string) => {
  console.log("Fetching markdown content:", path);
  return "";
};
